import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, CheckCircle2, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  // Internal
  full_name: '',
  email: '',
  phone: '',
  mailing_address: '',
  country: '',
  // Public
  display_name: '',
  title: '',
  location_display: '',
  organization: '',
  photo_url: '',
  linkedin: '',
  website: '',
  public_email: '',
  short_bio: '',
  long_bio: '',
  why_joined: '',
  expertise_areas: '',
  company_fax: '',
};

const emptyFundingRow = {
  funding_world: '', organisation: '', funder_type: '', contact_name: '', contact_role: '',
  relationship_nature: '', access_depth: '', contacts_at_org: '', typical_ticket: '',
  regions: [], willingness: '', notes: '',
};

const OnboardingPage = () => {
  const [formData, setFormData] = useState(initialState);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ref, setRef] = useState({
    funding_worlds: [], funder_types: {}, access_depth: [], ticket_bands: [],
    contacts_at_org: [], funding_regions: [], funding_willingness: [],
  });
  const [fundingRows, setFundingRows] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/network/reference-data`).then((r) => setRef(r.data)).catch(() => {});
  }, []);

  const setRow = (i, patch) =>
    setFundingRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const toggleRowRegion = (i, region) =>
    setFundingRows((rows) => rows.map((r, idx) => (idx === i ? {
      ...r,
      regions: r.regions.includes(region)
        ? r.regions.filter((x) => x !== region)
        : [...r.regions, region],
    } : r)));

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Photo must be under 8 MB.');
      return;
    }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Photo must be JPEG, PNG, or WebP.');
      return;
    }

    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        const absoluteUrl = res.data.url.startsWith('http')
          ? res.data.url
          : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setFormData((prev) => ({ ...prev, photo_url: absoluteUrl }));
        setPhotoPreview(absoluteUrl);
        toast.success('Photo uploaded.');
      } else {
        toast.error('Upload failed.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.photo_url) {
      toast.error('Please upload a profile photo.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/onboarding/submit`, {
        ...formData,
        funding_connections: fundingRows.filter((r) => r.organisation.trim() && r.funding_world),
      });
      if (res.data?.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-slate-50 min-h-screen" data-testid="onboarding-success">
        <section className="bg-slate-900 text-white py-20 md:py-28">
          <div className="max-w-3xl mx-auto container-padding text-center">
            <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <p className="caption text-cyan-400 mb-3">Application Received</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Your application is recorded.
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              The Council will review your application and contact you directly.
              You will hear from us within 10 business days.
            </p>
          </div>
        </section>
        <section className="py-12 text-center">
          <Link to="/" className="btn-primary" data-testid="onboarding-home-link">
            Return to SAFE Standard
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="onboarding-page">
      <section className="bg-slate-900 text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4">Council Onboarding</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Join the SAFE Council.
          </h1>
          <p className="text-base md:text-lg text-slate-300">
            This form captures the information required for both internal records
            and public listing. Internal details are never displayed publicly.
            Public profile fields appear on the SAFE Council page once approved.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto container-padding">
          <form onSubmit={handleSubmit} className="space-y-12" data-testid="onboarding-form">
            {/* Honeypot — hidden from humans, filled only by bots */}
            <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="company_fax">Company fax</label>
              <input
                type="text"
                id="company_fax"
                name="company_fax"
                tabIndex={-1}
                autoComplete="off"
                value={formData.company_fax}
                onChange={handleChange}
                data-testid="ob-honeypot"
              />
            </div>
            {/* INTERNAL SECTION */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-2">
                <p className="caption text-slate-500">Section 1</p>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Internal contact details</h2>
              <p className="text-sm text-slate-500 mb-6">Never displayed publicly. Used only by the SAFE Council for verification and correspondence.</p>
              <div className="space-y-5">
                <Field label="Full legal name" required>
                  <input name="full_name" required value={formData.full_name} onChange={handleChange} className={inputClass} data-testid="ob-full-name" />
                </Field>
                <Field label="Email" required>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} data-testid="ob-email" />
                </Field>
                <Field label="Phone (including country code)" required>
                  <input name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} data-testid="ob-phone" placeholder="+1 555 123 4567" />
                </Field>
                <Field label="Mailing address" required>
                  <textarea name="mailing_address" required value={formData.mailing_address} onChange={handleChange} rows={2} className={inputClass} data-testid="ob-address" />
                </Field>
                <Field label="Country" required>
                  <input name="country" required value={formData.country} onChange={handleChange} className={inputClass} data-testid="ob-country" />
                </Field>
              </div>
            </div>

            {/* PUBLIC SECTION */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <p className="caption text-cyan-700 mb-2">Section 2 — Public Profile</p>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Public profile</h2>
              <p className="text-sm text-slate-500 mb-6">All fields below will be visible on the SAFE Council page (https://safestandard.ai/council) after approval.</p>

              {/* Photo */}
              <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Profile photo <span className="text-red-500">*</span>
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoSelect} className="hidden" data-testid="ob-photo-input" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={photoUploading} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700 disabled:opacity-50" data-testid="ob-photo-btn">
                      <Upload className="w-3 h-3 inline mr-2" />
                      {photoUploading ? 'Uploading…' : photoPreview ? 'Replace photo' : 'Upload photo'}
                    </button>
                    <p className="text-xs text-slate-500 mt-2">Square crop preferred. JPEG, PNG, or WebP. Maximum 8 MB.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <Field label="Display name (how your name appears publicly)" hint="Leave blank to use your legal name.">
                  <input name="display_name" value={formData.display_name} onChange={handleChange} className={inputClass} data-testid="ob-display-name" placeholder={formData.full_name || 'e.g. Dr. Jane Smith'} />
                </Field>
                <Field label="Title / role" required hint="e.g. 'AI Governance Architect' or 'Senior Council Advisor'.">
                  <input name="title" required value={formData.title} onChange={handleChange} className={inputClass} data-testid="ob-title" />
                </Field>
                <Field label="Location (displayed publicly)" required hint="e.g. 'Geneva, Switzerland' or 'New York, USA'.">
                  <input name="location_display" required value={formData.location_display} onChange={handleChange} className={inputClass} data-testid="ob-location" />
                </Field>
                <Field label="Organization or affiliation">
                  <input name="organization" value={formData.organization} onChange={handleChange} className={inputClass} data-testid="ob-org" />
                </Field>
                <Field label="LinkedIn profile URL">
                  <input name="linkedin" value={formData.linkedin} onChange={handleChange} className={inputClass} data-testid="ob-linkedin" placeholder="https://www.linkedin.com/in/..." />
                </Field>
                <Field label="Personal website (optional)">
                  <input name="website" value={formData.website} onChange={handleChange} className={inputClass} data-testid="ob-website" placeholder="https://..." />
                </Field>
                <Field label="Public-facing email (optional)" hint="Different from internal email above. Leave blank to keep contact private.">
                  <input type="email" name="public_email" value={formData.public_email} onChange={handleChange} className={inputClass} data-testid="ob-public-email" />
                </Field>
                <Field label="Short bio" required hint="One paragraph (~80 words). Appears on your Council card.">
                  <textarea name="short_bio" required maxLength={1200} value={formData.short_bio} onChange={handleChange} rows={4} className={inputClass} data-testid="ob-short-bio" />
                </Field>
                <Field label="Full bio (optional)" hint="Longer biographical text shown in the profile modal.">
                  <textarea name="long_bio" maxLength={6000} value={formData.long_bio} onChange={handleChange} rows={6} className={inputClass} data-testid="ob-long-bio" />
                </Field>
                <Field label="Why I joined the SAFE Council" required hint="Your statement of intent. Used by the Council in review.">
                  <textarea name="why_joined" required value={formData.why_joined} onChange={handleChange} rows={4} className={inputClass} data-testid="ob-why-joined" />
                </Field>
                <Field label="Areas of expertise" hint="Comma-separated list. e.g. 'AI governance, child safety, regulatory policy'.">
                  <input name="expertise_areas" value={formData.expertise_areas} onChange={handleChange} className={inputClass} data-testid="ob-expertise" />
                </Field>
              </div>
            </div>

            {/* FUNDING ACCESS — optional */}
            <div className="bg-white border border-slate-200 p-6 md:p-8" data-testid="ob-funding-section">
              <p className="caption text-cyan-700 mb-2">Section 3 — Optional</p>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Funding &amp; investment access</h2>
              <p className="text-sm text-slate-500 mb-6">
                Never displayed publicly. SAFE needs capital from two different worlds — commercial
                investment and philanthropic funding. If you can reach funders in either, add one
                entry per organisation so we can see who in the Council can reach whom. Optional,
                and nobody is ever approached without speaking to you first.
              </p>

              <div className="space-y-6">
                {fundingRows.map((row, i) => (
                  <div key={i} className="border border-slate-200 bg-slate-50 p-5 space-y-4" data-testid={`ob-funding-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Connection {i + 1}
                      </div>
                      <button type="button" onClick={() => setFundingRows(fundingRows.filter((_, idx) => idx !== i))}
                        className="text-slate-400 hover:text-rose-600 transition-colors"
                        data-testid={`ob-funding-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Kind of funding">
                        <select value={row.funding_world}
                          onChange={(e) => setRow(i, { funding_world: e.target.value, funder_type: '' })}
                          className={inputClass} data-testid={`ob-funding-world-${i}`}>
                          <option value="">Select</option>
                          {(ref.funding_worlds || []).map((w) => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </Field>
                      <Field label="Organisation name">
                        <input value={row.organisation} onChange={(e) => setRow(i, { organisation: e.target.value })}
                          className={inputClass} data-testid={`ob-funding-org-${i}`} placeholder="e.g. Ford Foundation" />
                      </Field>
                      <Field label="Type of funder">
                        <select value={row.funder_type} onChange={(e) => setRow(i, { funder_type: e.target.value })}
                          className={inputClass} data-testid={`ob-funding-type-${i}`}>
                          <option value="">{row.funding_world ? 'Select a type' : 'Choose a kind first'}</option>
                          {((ref.funder_types || {})[row.funding_world] || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Typical amount they fund or invest">
                        <select value={row.typical_ticket} onChange={(e) => setRow(i, { typical_ticket: e.target.value })}
                          className={inputClass} data-testid={`ob-funding-ticket-${i}`}>
                          <option value="">Select a band</option>
                          {(ref.ticket_bands || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Who do you know there?">
                        <input value={row.contact_name} onChange={(e) => setRow(i, { contact_name: e.target.value })}
                          className={inputClass} data-testid={`ob-funding-contact-${i}`} />
                      </Field>
                      <Field label="Their role">
                        <input value={row.contact_role} onChange={(e) => setRow(i, { contact_role: e.target.value })}
                          className={inputClass} data-testid={`ob-funding-role-${i}`} />
                      </Field>
                    </div>

                    <Field label="How far does your access go?">
                      <select value={row.access_depth} onChange={(e) => setRow(i, { access_depth: e.target.value })}
                        className={inputClass} data-testid={`ob-funding-depth-${i}`}>
                        <option value="">Select</option>
                        {(ref.access_depth || []).map((d) => <option key={d.label} value={d.label}>{d.label}</option>)}
                      </select>
                    </Field>

                    <Field label="Nature of the relationship">
                      <textarea value={row.relationship_nature} rows={2}
                        onChange={(e) => setRow(i, { relationship_nature: e.target.value })}
                        className={inputClass} data-testid={`ob-funding-nature-${i}`} />
                    </Field>

                    <Field label="Regions this connection can reach">
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-1">
                        {(ref.funding_regions || []).map((r, ri) => (
                          <label key={r} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer normal-case tracking-normal">
                            <input type="checkbox" checked={row.regions.includes(r)}
                              onChange={() => toggleRowRegion(i, r)}
                              data-testid={`ob-funding-region-${i}-${ri}`}
                              className="w-4 h-4 accent-slate-900" />
                            {r}
                          </label>
                        ))}
                      </div>
                    </Field>

                    <Field label="What would you be comfortable doing?">
                      <select value={row.willingness} onChange={(e) => setRow(i, { willingness: e.target.value })}
                        className={inputClass} data-testid={`ob-funding-willingness-${i}`}>
                        <option value="">Select</option>
                        {(ref.funding_willingness || []).map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </Field>
                  </div>
                ))}
              </div>

              <button type="button" onClick={() => setFundingRows([...fundingRows, { ...emptyFundingRow }])}
                className="mt-6 inline-flex items-center gap-2 border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="ob-funding-add">
                <Plus className="w-3.5 h-3.5" />
                {fundingRows.length === 0 ? 'Add a funding connection' : 'Add another connection'}
              </button>
            </div>

            <div className="pt-2">
              <button type="submit" disabled={submitting || photoUploading} className={`btn-primary w-full md:w-auto ${(submitting || photoUploading) ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="ob-submit-btn">
                {submitting ? 'Submitting…' : 'Submit application'}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                By submitting, you confirm the information is accurate and consent to public display of the Section 2 fields if your application is approved.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

const inputClass = 'w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 transition-colors bg-white';

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export default OnboardingPage;
